import { observable, action, computed, makeObservable } from 'mobx';
import { CountryBudgetDecision } from 'nomst/Stores/Decision/countryBudgetDecision';
import { Employee } from 'nomst/Stores/Decision/employee';
import { EmployeeSort } from 'nomst/Stores/Decision/Constants/employeeSort';
import { DecisionDispatcher } from 'nomst/Stores/Decision/decision.dispatcher';
import { ReportsView, NavigationErrors, FeatureFlags, EntityTypes } from 'typings/enums';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { TeamDecisionDispatcher } from 'nomst/Stores/Decision/teamDecision.dispatcher';
import {
    dataManagerFactory,
    navigateToNoMst,
    managerListSidePanelStore,
    errorMessages } from 'utils/MobxDataManager/singletons';
import Coalesce from 'nomst/Stores/Utility/coalesce';
import { IEmployee, ISearchedEmployee } from 'typings/employee';
import { ICountryBudgetDecision } from 'typings/countryBudgetDecision';
import { featureFlagHelper } from 'singletons';
import { IPrintServiceDataManager } from 'typings/dataManagers';
import { sleep } from 'utils/timeoutUtils';

export class TeamDecisionStore {

    @observable employeeSort: EmployeeSort = EmployeeSort.NameSortOption;

    @observable currentIsoCurrencyCode: string;

    @observable showPromotionsOnly: boolean = false;

    @observable teamView: ReportsView = ReportsView.DirectsOnly;

    @observable hasSelectedTeam: boolean;

    @observable filterByJob: string;

    @observable filterByDepartment: string;

    @observable managerSourceIds: string[];

    @observable pdfPrintStatus: IPrintPollStatus;

    readonly teamDecisionDispatcher: TeamDecisionDispatcher;

    @observable private _countryBudgetDecisions: ICountryBudgetDecision[] = [];

    @observable private _budgetNamesMap: Map<number, string> = new Map<number, string>();

    @observable private _filteredCountryBudgetDecisions: ICountryBudgetDecision[] = [];

    readonly decisionDispatcher: DecisionDispatcher;

    private readonly _configuration: Configuration;

    private readonly _printServiceDataManager: IPrintServiceDataManager;

    constructor(configuration: Configuration, decisionDispatcher: DecisionDispatcher) {
        makeObservable(this);
        this._configuration = configuration;
        this.decisionDispatcher = decisionDispatcher;
        this.teamDecisionDispatcher = new TeamDecisionDispatcher(this);

        this._countryBudgetDecisions = [];
        this._filteredCountryBudgetDecisions = [];
        this.filterByJob = '';
        this.filterByDepartment = '';
        this._printServiceDataManager = dataManagerFactory.getPrintServiceDataManager();
        this.resetPdfPrintStatus();
    }

    get countryBudgetDecisions(): ICountryBudgetDecision[] {
        return this._filteredCountryBudgetDecisions && this._filteredCountryBudgetDecisions.length ?
            this._filteredCountryBudgetDecisions : this._countryBudgetDecisions;
    }

    @action
    clearCountryBudgetDecisions(): void {
        this._countryBudgetDecisions = [];
        this._budgetNamesMap = new Map<number, string>();
    }

    @action
    setTeamSelection(hasSelectedTeam: boolean): void {
        this.hasSelectedTeam = hasSelectedTeam;
    }

    @action
    setTeamView(selectedView: ReportsView): void {
        this.teamView = selectedView;
    }

    @action
    setEmployeeView(selectedEmployeeSourceId: Nullable<string>): Promise<void> {
        if (selectedEmployeeSourceId) {
            if (!this._countryBudgetDecisions) {
                return Promise.resolve();
            }

            this._countryBudgetDecisions.forEach(countryBudgetDecision => {
                const employee = countryBudgetDecision.filterOnEmployee(selectedEmployeeSourceId);
                if (employee && countryBudgetDecision.countryBudgetDecisionDispatcher) {
                    return countryBudgetDecision.countryBudgetDecisionDispatcher
                        .dispatchEmployeeSelection(selectedEmployeeSourceId);
                }
                return Promise.resolve();
            });
            return Promise.resolve();
        }
        return Promise.resolve();
    }

    @action
    async fetchCountryBudgetDecisions(managerSourceIds: string[]): Promise<void> {
        if (!managerSourceIds) {
            return;
        }

        this.managerSourceIds = managerSourceIds;
        const employeesPerPage = featureFlagHelper?.featureFlagSettings[FeatureFlags.EmployeesPerPage] as number;
        const countryBudgetDecisions = Coalesce.array(
            (await dataManagerFactory.getManagerDataManager().getCountryBudgetDecisions(
                this._configuration.masterAccountId,
                this._configuration.cycleId,
                this.managerSourceIds,
                this.isDirectOnly,
                this.employeeSort,
                employeesPerPage,
                this.showPromotionsOnly)) || []);

        if (!countryBudgetDecisions) {
            return Promise.resolve();
        }

        const filtered = countryBudgetDecisions.filter(budget => budget.employees.length > 0);
        if (!filtered.length
            && managerListSidePanelStore.isFirstLevelManager
            && !this._configuration.teamPageViews.isHideIneligibleEmployeesChecked) {
            errorMessages.setErrorMessage(NavigationErrors.NoReports);
            return navigateToNoMst.navigateToErrorMessageView();
        }

        let employees: Employee[] = [];
        employees = employees.concat(
            ...countryBudgetDecisions
                .map((countryBudgetDecision: ICountryBudgetDecision) => countryBudgetDecision.employees));

        const employeeSourceIds = employees.map(employee => employee.employeeSourceId);

        const conversationSummaries = Coalesce.array(
            (await dataManagerFactory.getConversationDataManager().getConversationSummaries(
                this._configuration.masterAccountId,
                EntityTypes.Employee,
                employeeSourceIds)) || []);

        this._countryBudgetDecisions = countryBudgetDecisions.map((countryBudgetDecision: ICountryBudgetDecision) => {
            countryBudgetDecision.showPromotionsOnly = this.showPromotionsOnly;
            return new CountryBudgetDecision(
                countryBudgetDecision,
                conversationSummaries,
                this._configuration,
                this.decisionDispatcher);
        });

        // All budgets exist for all countryBudgetDecisions. So just read first one and populate the names
        this._budgetNamesMap = this.countryBudgetDecisions[0]?.budgetsMap || new Map<number, string>();
        this.decisionDispatcher.dispatchSetBudgetNamesMapAsync(this._budgetNamesMap);
    }

    allEmployees(): Employee[] {
        const employees: Employee[] = [];
        return employees.concat(
            ...this._countryBudgetDecisions
                .map((countryBudgetDecision: ICountryBudgetDecision) => countryBudgetDecision.employees));
    }

    @computed
    get countriesOverBudgetsMap(): Map<number, ICountryBudget> {
        const countriesOverBudgetsMap = new Map<number, ICountryBudget>();

        if (!this.countryBudgetDecisions) {
            return countriesOverBudgetsMap;
        }

        let countriesOverBudgets: ICountryBudget[] = [];
        countriesOverBudgets = countriesOverBudgets.concat(
            ...this._countryBudgetDecisions.map((countryBudgetDecision: ICountryBudgetDecision) =>
                countryBudgetDecision.overBudgetCountryBudgets));

        if (countriesOverBudgets != null) {
            countriesOverBudgets.forEach(overBudget => {
                if (countriesOverBudgetsMap.has(overBudget.budgetCycleId)) {
                    return;
                }

                countriesOverBudgetsMap.set(overBudget.budgetCycleId, overBudget);
            });
        }
        return countriesOverBudgetsMap;
    }

    @computed
    get isDirectOnly(): boolean {
        return this.teamView === ReportsView.DirectsOnly;
    }

    @computed
    get countriesOverBudgets(): ICountryBudget[] {
        return Array.from(this.countriesOverBudgetsMap ? this.countriesOverBudgetsMap.values() : []);
    }

    @computed
    get countryBudgetsNeedPages(): boolean {
        if (!this._countryBudgetDecisions) {
            return false;
        }
        let needsPages = false;
        this._countryBudgetDecisions.forEach(countryBudgetDecision => {
            if (countryBudgetDecision.employeesNeedFetched) {
                needsPages = true;
                return;
            }
        });
        return needsPages;
    }

    setSearchedEmployee(employeeId: string): void {
        if ( !this._countryBudgetDecisions) {
            return;
        }
        this._countryBudgetDecisions.forEach(countryBudgetDecision => {
            const employee = countryBudgetDecision.filterOnEmployee(employeeId);
            if (employee) {
                this._filteredCountryBudgetDecisions = [countryBudgetDecision];
                return;
            }
        });
    }

    // This will filter our employees quickly on the front end if we're not using paging (small amount of employees)
    // but will use the backend for filtering if we are using paging (lots of employees)
    @action
    async setSearchedFilter(
        jobFilter: Nullable<string>,
        departmentFilter: Nullable<string>,
        initialCall: boolean = false): Promise<void> {
        if ( !this._countryBudgetDecisions) {
            return;
        }
        jobFilter ? this.filterByJob = jobFilter : null;
        departmentFilter ? this.filterByDepartment = departmentFilter : null;
        const employeesObject: {[key: string]: IEmployee[]} = {};
        // If we have pages on the backend then we're going to use the server to get all matching jobs throughout all countries
        // and group them up by country so that we can place them in the appropriate countryBudget
        if (this.countryBudgetsNeedPages && !initialCall) {
            const searchedEmployees = await this.getPagedEmployees(this.isDirectOnly);
            searchedEmployees.forEach(employee => {
                employeesObject[employee.countryCode ?? ''] ?
                    employeesObject[employee.countryCode ?? ''].push(employee) : employeesObject[employee.countryCode ?? ''] = [employee];
            });
        }
        this._countryBudgetDecisions.forEach(countryBudgetDecision => {
            countryBudgetDecision.setJobAndDepartmentFilter(this.filterByJob, this.filterByDepartment);
            countryBudgetDecision.removeEmployeeSelection();
            // If using the server for paging then place the fetched employees in the appropriate countryBudget
            if (this.countryBudgetsNeedPages && !initialCall) {
                const employees = employeesObject[countryBudgetDecision.code];
                if (employees) {
                    countryBudgetDecision.setEmployees(employees);
                }
            }
        });
    }

    private async getPagedEmployees(isDirectsOnly: boolean): Promise<IEmployee[]> {
        const dataManager = dataManagerFactory.getManagerDataManager();
        const employees = await dataManager.getPagedEmployees(
            this._configuration.masterAccountId,
            this._configuration.cycleId,
            managerListSidePanelStore.selectedManagerIds,
            isDirectsOnly,
            this.employeeSort,
            0,
            -1, // need this to be negative 1 in order to grab employees across all countries
            '',
            0,
            this.showPromotionsOnly,
            this.filterByJob,
            this.filterByDepartment
        );
        return employees ?? [];
    }

    @action
    clearSearchedFilter(jobTitle: boolean): void {
        if (!this._countryBudgetDecisions) {
            return;
        }
        jobTitle ? this.filterByJob = '' : this.filterByDepartment = '';
        this._countryBudgetDecisions.forEach(countryBudgetDecision => {
            jobTitle ? countryBudgetDecision.clearFilterByJob() : countryBudgetDecision.clearFilterByDepartment();
        });

        if (!this.countryBudgetsNeedPages) {
            return;
        }
        // If we have cleared both filters then we need to get back the original view
        if (!this.filterByJob && !this.filterByDepartment) {
            this.fetchCountryBudgetDecisions(this.managerSourceIds);
        // If we're using paging (large amount of employees) and we still have a filter set then
        // refetch using the current filter set
        } else {
            this.getPagedEmployees(this.isDirectOnly);
        }
    }

    clearSearchedEmployee(): void {
        const countryBudget = this._filteredCountryBudgetDecisions && this._filteredCountryBudgetDecisions.pop();
        if (!countryBudget) {
            return;
        }

        countryBudget.removeFilterOnEmployee();
    }

    setTeamBudgetBarUsage(isUsingBudgetBar: boolean): void {
        if ( this._countryBudgetDecisions) {
            this._countryBudgetDecisions.forEach(countryBudgetDecision =>
                countryBudgetDecision.setBudgetBarUsage(isUsingBudgetBar));
        }
    }

    setTeamReviewability(isTeamReviewable: boolean): void {
        this._countryBudgetDecisions.forEach(countryBudgetDecision => {
            countryBudgetDecision.employees.forEach(employee =>
                employee.setReviewability(isTeamReviewable));
        });
    }

    handleDispatchIsoCurrencyCodeChanged(isoCurrencyCode: string): void {
        this.setSelectedCurrency(isoCurrencyCode);
        this._countryBudgetDecisions.forEach(countryBudgetDecision => {
            countryBudgetDecision.employees.forEach(employee => {
                employee.setCurrentCurrency(isoCurrencyCode);
            });

            countryBudgetDecision.budgets.forEach(countryBudget => {
                if (countryBudget && countryBudget.setCurrentCurrency) {
                    countryBudget.setCurrentCurrency(isoCurrencyCode);
                }
            });
        });
    }

    async searchEmployees(searchText: string, resultLimit: number, managerId: string[]): Promise<ISearchedEmployee[]> {
        return (await dataManagerFactory.getEmployeeDataManager().getSuggestedEmployees(
            this._configuration.masterAccountId,
            managerId,
            this.isDirectOnly,
            searchText,
            resultLimit
        )) || [];
    }

    async exportReportsToCsv(managerSourceIds: string[]): Promise<void> {
        const managerDataManager = dataManagerFactory.getManagerDataManager();
        if (!managerDataManager || !this._configuration) {
            return Promise.resolve();
        }

        await managerDataManager.exportReportsToCsv(
            this._configuration.masterAccountId,
            this._configuration.cycleId,
            managerSourceIds,
            this.isDirectOnly);
    }

    // for testing purposes only
    setCountryBudgetDecisionsTest(countryBudgetDecisions: ICountryBudgetDecision[]): void {
        this._countryBudgetDecisions = countryBudgetDecisions;
    }

    @action
    setSelectedSortFormat(employeeSortFormat: EmployeeSort): void {
        this.setEmployeeSortFormat(employeeSortFormat);
        this._countryBudgetDecisions.forEach( countryBudgetDecision =>
            countryBudgetDecision.setSortOptionAndUpdateFlag(true, employeeSortFormat, this.isDirectOnly));
    }

    handleDispatchEployeeSortOptionChanged(employeeSortFormat: EmployeeSort): void {
        this.setSelectedSortFormat(employeeSortFormat);
        navigateToNoMst.navigateToEmployeeSort(employeeSortFormat);
    }

    @action
    setEmployeeSortFormat(employeeSortFormat: EmployeeSort): void {
        this.employeeSort = employeeSortFormat;
    }

    @action
    private setSelectedCurrency(currentIsoCurrencyCode: string): void {
        this.currentIsoCurrencyCode = currentIsoCurrencyCode;
    }

    handleDispatchPromotionOnlyChanged(promotionOnly: boolean, initialCall: boolean = false): void {
        this.setShowPromotionsOnly(promotionOnly, initialCall);
        navigateToNoMst.navToUpdatePromotionToggle(promotionOnly);
    }

    @action
    setShowPromotionsOnly(promotionOnly: boolean, initialCall: boolean = false): void {
        this.showPromotionsOnly = promotionOnly;
        if (initialCall) {
            return;
        }
        this._countryBudgetDecisions.forEach(countryBudgetDecision =>
            countryBudgetDecision.setShowPromotionsOnly(promotionOnly, this.isDirectOnly));
    }

    async handleDispatchPdfPrintService(): Promise<void> {
        const test = await this._printServiceDataManager.requestPdfs(this._configuration.masterAccountId, this._configuration.cycleId, this.managerSourceIds);
        if (!test.requestId) {
            return;
        }

        await this.pollPrintDownload(test.requestId);

        // if nothing succeeded just return now
        if (!this.pdfPrintStatus.fileUrl.length) {
            return;
        } 
        await this._printServiceDataManager.downloadPdfs(
            this._configuration.masterAccountId,
            this._configuration.cycleId,
            this.pdfPrintStatus.fileUrl
        );
        this.setPdfCompletedStatus();
    }

    async pollPrintDownload(requestId: string): Promise<void> {
        let counter = 0;
        while(counter++ < 100) {
            const response = await this._printServiceDataManager.pollPdfProgress(
                this._configuration.masterAccountId,
                this._configuration.cycleId,
                requestId
            );

            this.setPdfPrintStatus(response);

            // when the call is completed it will eventually remove the total pages from the response
            // or watch for when the pages reach the total pages, whichever happens first.
            if (response?.curPage !== response?.totalPages && response?.totalPages) {
                await sleep(5000);
            } else {
                return;
            }
        }
        if (counter === 99) {
            this.setPdfCompletedStatus();
        }
    }

    @action
    resetPdfPrintStatus(): void {
        this.pdfPrintStatus = {
            curPage: 0,
            totalPages: 0,
            retryAfterMillisecondseUrl: 0,
            requestId: '',
            fileNames: [],
            failedFileNames: [],
            failedPageIds: [],
            fileUrl: [],
            completed: false
        };
    }

    @action
    setPdfPrintStatus(status: IPrintPollStatus): void {
        this.pdfPrintStatus = status;
    }

    @action
    setPdfCompletedStatus(): void {
        this.pdfPrintStatus.completed = true;
    }
}